import { useState } from "react"
import { ChevronsUpDown, Loader2 } from "lucide-react"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/ui/form"
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/ui/command"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Button } from "@components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover"
import { ScrollArea } from "@components/ui/scroll-area"
import { cn } from "@components/ui/lib/utils"
import {
  durationToMonths,
  useSerializeCustomOfferForm,
} from "./utils/useSerializeCustomOfferForm"
import { useCustomOfferSubmit } from "./utils/useCustomOfferSubmit"
import { CustomOfferPlanRewards } from "./CustomOfferPlanRewards"
import { useCustomOfferFormLogic } from "./utils/useCustomOfferFormLogic"

type TCustomOfferFormProps = {
  account_id: string
  case_guid: string
  setShowChangeOfferForm: (showForm: boolean) => void
}

export type TCustomOfferFormValues = {
  market_rate_id: string
  contract_type_code: string
  plan_duration: string
  plan_code: string
}

export const CustomOfferForm = ({
  account_id,
  case_guid,
  setShowChangeOfferForm
}: TCustomOfferFormProps) => {
  const [isPlanPopoverOpen, setIsPlanPopoverOpen] = useState(false)
  const [isMarketRatePopoverOpen, setIsMarketRatePopoverOpen] = useState(false)

  const {
    isPending,
    isError,
    planDurations,
    allPlans,
    contractTypes,
    marketRates,
    account,
  } = useSerializeCustomOfferForm(account_id)

  const {
    form,
    selectedContractType,
    selectedPlanDuration,
    selectedMarketRate,
    selectedPlan,
    filteredRates,
    filteredPlans,
    planSearchFilter,
    marketRateSearchFilter,
  } = useCustomOfferFormLogic({
    contractTypes,
    marketRates,
    allPlans,
  })

  const { customOfferOnSubmit, submitIsPending } = useCustomOfferSubmit({
    account,
    case_guid,
    setShowChangeOfferForm
  })

  const handleSubmit = async (values: TCustomOfferFormValues) => {
    customOfferOnSubmit(values)
  }

  if (isPending)
    return (
      <div className="tw:bordered-container">
        <Loader2 className="tw:flex tw:w-full tw:animate-spin tw:justify-center tw:text-gray-500" />
      </div>
    )
  if (isError)
    return (
      <div className="tw:text-red-500">
        There was an error loading data for form. Please try again.
      </div>
    )

  return (
    <div className="tw:bordered-container tw:flex tw:w-full tw:flex-col tw:gap-4 tw:2xl:w-2/3">
      <p className="tw:font-bold">Account ID: {account_id}</p>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="tw:flex tw:w-full tw:flex-col tw:gap-4"
        >
          <FormField
            control={form.control}
            name="contract_type_code"
            rules={{ required: "Contract type is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contract Type</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value)
                    form.resetField("plan_code")
                    form.resetField("market_rate_id")
                  }}
                  value={field.value}
                >
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select a contract type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {contractTypes?.map((contractType) => (
                      <SelectItem
                        key={contractType.contract_type_code}
                        value={contractType.contract_type_code}
                      >
                        {contractType.contract_type_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="plan_duration"
            rules={{ required: "Plan duration is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contract Duration</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value)
                    form.resetField("plan_code")
                  }}
                  value={field.value}
                >
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select a contract duration" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="tw:max-h-[300px] tw:overflow-y-auto">
                    {planDurations?.map((value) => (
                      <SelectItem key={value} value={value}>
                        {durationToMonths(value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="market_rate_id"
            rules={{
              required: "Market rate is required",
            }}
            render={({ field }) => (
              <FormItem className="tw:flex tw:w-full tw:flex-col">
                <FormLabel>Market Rate</FormLabel>
                <Popover
                  modal
                  open={isMarketRatePopoverOpen}
                  onOpenChange={setIsMarketRatePopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <div>
                      <FormControl className="tw:w-full">
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          disabled={!selectedContractType}
                          className={cn(
                            "tw:flex tw:justify-between",
                            !selectedMarketRate && "tw:text-muted-foreground",
                          )}
                        >
                          {selectedMarketRate?.rate ??
                            "Select a market rate..."}
                          <ChevronsUpDown className="tw:opacity-50" />
                        </Button>
                      </FormControl>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="tw:w-fit tw:p-0">
                    <Command filter={marketRateSearchFilter}>
                      <CommandInput placeholder="Search market rate..." />
                      <ScrollArea className="h-[200px]">
                        <CommandList>
                          {filteredRates?.map((market_rate) => (
                            <CommandItem
                              key={market_rate.market_rate_id}
                              value={String(market_rate.market_rate_id)}
                              onSelect={(value) => {
                                field.onChange(value)
                                setIsMarketRatePopoverOpen(false)
                              }}
                            >
                              {market_rate.rate}
                            </CommandItem>
                          ))}
                        </CommandList>
                      </ScrollArea>
                    </Command>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="plan_code"
            rules={{
              required: "Plan code is required",
            }}
            render={({ field }) => (
              <FormItem className="tw:flex tw:w-full tw:flex-col">
                <FormLabel>Plan</FormLabel>
                <Popover
                  modal
                  open={isPlanPopoverOpen}
                  onOpenChange={setIsPlanPopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <div>
                      <FormControl className="tw:w-full">
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          disabled={
                            !selectedContractType || !selectedPlanDuration
                          }
                          className={cn(
                            "tw:flex tw:justify-between",
                            !selectedPlan && "tw:text-muted-foreground",
                          )}
                        >
                          {selectedPlan?.plan_code ?? "Select a plan..."}
                          <ChevronsUpDown className="tw:opacity-50" />
                        </Button>
                      </FormControl>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="tw:w-fit tw:p-0">
                    <Command filter={planSearchFilter}>
                      <CommandInput placeholder="Search plan code..." />
                      <ScrollArea className="h-[200px]">
                        <CommandList>
                          {filteredPlans?.map((plan) => (
                            <CommandItem
                              key={plan.plan_code}
                              value={plan.plan_code}
                              onSelect={(value) => {
                                field.onChange(value)
                                setIsPlanPopoverOpen(false)
                              }}
                            >
                              {plan.plan_name} ({plan.plan_code})
                            </CommandItem>
                          ))}
                        </CommandList>
                      </ScrollArea>
                    </Command>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {selectedPlan && (
            <>
              <div>
                <h3>{selectedPlan.plan_name}</h3>
                <h5>
                  <span className="tw:capitalize">
                    {selectedContractType?.contract_type_code}
                  </span>{" "}
                  &#64; {selectedMarketRate?.rate} /{" "}
                  {selectedMarketRate?.unit_of_measure} for{" "}
                  {durationToMonths(selectedPlan?.contract_duration)} months
                </h5>
              </div>

              <CustomOfferPlanRewards plan_code={selectedPlan.plan_code} />
            </>
          )}

          <div className="tw:flex tw:gap-4">
            <Button
              type="submit"
              disabled={!form.formState.isValid || submitIsPending}
            >
              Submit
            </Button>
            <Button
              variant="warning"
              type="button"
              onClick={() => form.reset()}
            >
              Reset Fields
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
